/**
 * NeonVariant — 80s synthwave/retro aesthetic. Magenta-cyan neon glow,
 * sunset horizon, vanishing-point grid floor, chrome slogan.
 */
import { motion } from "framer-motion";
import Wordmark from "./Wordmark";
import { COPY } from "./copy";

export default function NeonVariant({ portrait }) {
  const headlineSize = portrait ? "text-[140px]" : "text-[125px]";

  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: "#0a0118" }}>
      <div
        className="absolute top-0 left-0 right-0 h-[55%]"
        style={{ background: "linear-gradient(180deg, #0a0118 0%, #2A0E55 35%, #832C8D 70%, #F472B6 88%, #FBBF24 100%)" }}
      />
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 rounded-full"
        style={{
          width: portrait ? 520 : 460,
          height: portrait ? 520 : 460,
          top: portrait ? "32%" : "28%",
          background: "radial-gradient(circle, #FCD34D 0%, #F472B6 35%, #A855F7 70%, transparent 95%)",
          boxShadow: "0 0 120px rgba(244,114,182,0.6), 0 0 200px rgba(168,85,247,0.4)",
        }}
        animate={{ scale: [1, 1.04, 1], opacity: [0.85, 1, 0.85] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      {[...Array(6)].map((_, i) => (
        <div key={i} className="absolute left-0 right-0 bg-[#0a0118]" style={{ top: `${30 + i * 3}%`, height: 2 + i }} />
      ))}

      <div
        className="absolute bottom-0 left-0 right-0 h-[55%] origin-top"
        style={{ perspective: "600px", perspectiveOrigin: "50% 0%" }}
      >
        <motion.div
          className="absolute inset-0"
          style={{
            transform: "rotateX(60deg)",
            backgroundImage:
              "linear-gradient(to right, rgba(244,114,182,0.7) 0 2px, transparent 2px), linear-gradient(to bottom, rgba(34,211,238,0.6) 0 2px, transparent 2px)",
            backgroundSize: "100px 60px",
            backgroundPosition: "0 0",
          }}
          animate={{ backgroundPosition: ["0px 0px", "0px 60px"] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0a0118 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-16">
        <Wordmark
          size={portrait ? "xl" : "lg"}
          glpColor="#FFFFFF"
          floColor="#FCD34D"
          shadow="0 0 4px #fff, 0 0 14px #F472B6, 0 0 30px rgba(244,114,182,0.7), 4px 4px 0 #831843"
          delay={0.3}
        />
        <motion.h1
          initial={{ opacity: 0, y: 30, letterSpacing: "0.1em" }}
          animate={{ opacity: 1, y: 0, letterSpacing: "0em" }}
          transition={{ delay: 0.8, duration: 1.0, ease: "easeOut" }}
          className={`${headlineSize} font-black leading-[0.88] tracking-tight mt-4`}
          style={{
            color: "#fff",
            textShadow:
              "0 0 4px #fff, 0 0 12px #F472B6, 0 0 24px #F472B6, 0 0 60px rgba(244,114,182,0.7), 4px 4px 0 #831843",
            fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
          }}
        >
          {COPY.headline1}
          <br />
          <span
            style={{
              color: "#FCD34D",
              textShadow:
                "0 0 4px #FCD34D, 0 0 14px #F59E0B, 0 0 30px rgba(245,158,11,0.7), 4px 4px 0 #7C2D12",
            }}
          >
            {COPY.headline2}
          </span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 0.7 }}
          className="text-3xl mt-8 font-bold uppercase tracking-[0.2em]"
          style={{ color: "#22d3ee", textShadow: "0 0 8px #22d3ee, 0 0 20px rgba(34,211,238,0.7)" }}
        >
          {COPY.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.4, duration: 0.8 }}
          className="mt-8 max-w-[840px] text-white"
        >
          <p className="text-xl font-bold mb-2" style={{ textShadow: "0 0 8px rgba(244,114,182,0.7)" }}>
            {COPY.body1}
          </p>
          <p className="text-lg text-pink-100 mb-2">{COPY.body2}</p>
          <p className="text-base text-cyan-100">{COPY.body3}</p>
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 3.3, duration: 0.5 }}
        className="absolute bottom-10 left-0 right-0 text-center text-sm tracking-[0.4em] uppercase font-bold"
        style={{ color: "#F472B6", textShadow: "0 0 8px #F472B6, 0 0 20px rgba(244,114,182,0.6)" }}
      >
        {COPY.footer} ·{" "}
        <span style={{ color: "#22d3ee", textShadow: "0 0 8px #22d3ee" }}>{COPY.url}</span>
      </motion.p>
    </div>
  );
}
